<template>
  <div class="cookie-policy">
    <div class="container">
      <div class="header">
        <h1>Cookie Policy</h1>
        <p class="last-updated">Last Updated: November 6, 2025</p>
      </div>

      <div class="content">
        <section>
          <h2>1. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are placed on your device (computer, smartphone, tablet) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
          </p>
          <p>
            Cookies help us remember your preferences, understand how you use our platform, and improve your experience.
          </p>
        </section>

        <section>
          <h2>2. How We Use Cookies</h2>
          <p>InvestBuddy.ai uses cookies for the following purposes:</p>
          <ul>
            <li><strong>Authentication:</strong> Keep you logged in to your account</li>
            <li><strong>Preferences:</strong> Remember your settings and preferences</li>
            <li><strong>Security:</strong> Detect and prevent fraudulent activity</li>
            <li><strong>Analytics:</strong> Understand how you use our platform</li>
            <li><strong>Performance:</strong> Improve loading times and functionality</li>
            <li><strong>Marketing:</strong> Deliver relevant content and measure effectiveness</li>
          </ul>
        </section>

        <section>
          <h2>3. Types of Cookies We Use</h2>

          <h3>3.1 Strictly Necessary Cookies</h3>
          <p class="cookie-category essential">
            <span class="tag">Required</span>
            These cookies are essential for the platform to function. You cannot opt out of these cookies.
          </p>
          <table class="cookie-table">
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>auth_token</td>
                <td>Authentication - keeps you logged in</td>
                <td>7 days</td>
              </tr>
              <tr>
                <td>session_id</td>
                <td>Session management</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>csrf_token</td>
                <td>Security - prevents cross-site request forgery</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>cookie_consent</td>
                <td>Stores your cookie preferences</td>
                <td>1 year</td>
              </tr>
            </tbody>
          </table>

          <h3>3.2 Functional Cookies</h3>
          <p class="cookie-category functional">
            <span class="tag">Functional</span>
            These cookies enhance functionality and personalization. The platform can function without them, but with reduced features.
          </p>
          <table class="cookie-table">
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>user_preferences</td>
                <td>Stores display preferences (theme, language, etc.)</td>
                <td>1 year</td>
              </tr>
              <tr>
                <td>portfolio_view</td>
                <td>Remembers your preferred portfolio view</td>
                <td>30 days</td>
              </tr>
              <tr>
                <td>notification_prefs</td>
                <td>Stores notification preferences</td>
                <td>6 months</td>
              </tr>
            </tbody>
          </table>

          <h3>3.3 Analytics Cookies</h3>
          <p class="cookie-category analytics">
            <span class="tag">Analytics</span>
            These cookies help us understand how users interact with our platform so we can improve it.
          </p>
          <table class="cookie-table">
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>_ga</td>
                <td>Google Analytics - user identification</td>
                <td>2 years</td>
              </tr>
              <tr>
                <td>_gid</td>
                <td>Google Analytics - session tracking</td>
                <td>24 hours</td>
              </tr>
              <tr>
                <td>analytics_session</td>
                <td>Internal analytics tracking</td>
                <td>30 days</td>
              </tr>
            </tbody>
          </table>

          <h3>3.4 Marketing Cookies</h3>
          <p class="cookie-category marketing">
            <span class="tag">Marketing</span>
            These cookies track your activity to deliver personalized marketing and measure campaign effectiveness.
          </p>
          <table class="cookie-table">
            <thead>
              <tr>
                <th>Cookie Name</th>
                <th>Purpose</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>marketing_id</td>
                <td>Tracks marketing campaign effectiveness</td>
                <td>90 days</td>
              </tr>
              <tr>
                <td>referrer</td>
                <td>Tracks how you found our platform</td>
                <td>30 days</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>4. Third-Party Cookies</h2>
          <p>
            We use services from third parties that may set their own cookies. These include:
          </p>

          <h3>4.1 Analytics Services</h3>
          <ul>
            <li><strong>Google Analytics:</strong> Website usage analytics (<a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>)</li>
          </ul>

          <h3>4.2 Payment Processors</h3>
          <ul>
            <li><strong>Stripe:</strong> Payment processing for subscriptions (<a href="https://stripe.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>)</li>
          </ul>

          <h3>4.3 Communication Services</h3>
          <ul>
            <li><strong>Twilio:</strong> Phone verification services (<a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener">Privacy Policy</a>)</li>
          </ul>

          <h3>4.4 OAuth Providers</h3>
          <ul>
            <li><strong>Google OAuth:</strong> Sign in with Google (<a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>)</li>
          </ul>
        </section>

        <section>
          <h2>5. Managing Your Cookie Preferences</h2>

          <h3>5.1 Cookie Consent Banner</h3>
          <p>
            When you first visit InvestBuddy.ai, you'll see a cookie consent banner. You can:
          </p>
          <ul>
            <li><strong>Accept All:</strong> Allow all cookies for the best experience</li>
            <li><strong>Reject Optional:</strong> Only use strictly necessary cookies</li>
            <li><strong>Customize:</strong> Choose which categories of cookies to accept</li>
          </ul>

          <h3>5.2 Change Your Preferences</h3>
          <p>You can change your cookie preferences at any time:</p>
          <div class="preference-actions">
            <button @click="showCookieSettings" class="btn-primary">
              <span class="icon">🍪</span>
              Manage Cookie Preferences
            </button>
          </div>

          <h3>5.3 Browser Settings</h3>
          <p>
            You can also control cookies through your browser settings:
          </p>
          <ul>
            <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
            <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
            <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
            <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
          </ul>
          <p>
            Note: Blocking all cookies may prevent some features from working properly.
          </p>
        </section>

        <section>
          <h2>6. Local Storage and Session Storage</h2>
          <p>
            In addition to cookies, we use browser storage technologies:
          </p>
          <ul>
            <li><strong>Local Storage:</strong> Stores preferences and cached data locally</li>
            <li><strong>Session Storage:</strong> Temporary storage cleared when you close your browser</li>
          </ul>
          <p>
            These technologies help improve performance and user experience but don't track you across different websites.
          </p>
        </section>

        <section>
          <h2>7. Do Not Track (DNT)</h2>
          <p>
            Some browsers include a "Do Not Track" (DNT) feature. While we respect your privacy choices, there is no universal standard for DNT signals. We recommend using our cookie consent tools to manage your preferences.
          </p>
        </section>

        <section>
          <h2>8. Cookies and Mobile Apps</h2>
          <p>
            If we release a mobile app in the future, similar tracking technologies may be used. Mobile-specific identifiers will be managed through your device settings.
          </p>
        </section>

        <section>
          <h2>9. Updates to This Cookie Policy</h2>
          <p>
            We may update this Cookie Policy to reflect changes in technology or legal requirements. The "Last Updated" date at the top indicates when changes were made.
          </p>
        </section>

        <section>
          <h2>10. Contact Us</h2>
          <p>
            If you have questions about our use of cookies, please contact us:
          </p>
          <div class="contact-info">
            <p><strong>Email:</strong> support@investbuddy.ai</p>
            <p><strong>Contact Form:</strong> <router-link to="/contact">Contact Us</router-link></p>
          </div>
        </section>

        <section>
          <h2>11. Related Policies</h2>
          <p>For more information about how we handle your data:</p>
          <ul>
            <li><router-link to="/privacy-policy">Privacy Policy</router-link> - How we collect and use your data</li>
            <li><router-link to="/terms-of-service">Terms of Service</router-link> - Terms governing platform use</li>
          </ul>
        </section>
      </div>

      <div class="footer-actions">
        <router-link to="/privacy-policy" class="btn-secondary">Privacy Policy</router-link>
        <button @click="showCookieSettings" class="btn-primary">
          Manage Cookies
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

onMounted(() => {
  window.scrollTo(0, 0)
})

const showCookieSettings = () => {
  // Emit event to show cookie consent banner
  window.dispatchEvent(new CustomEvent('show-cookie-settings'))
}
</script>

<style scoped>
.cookie-policy {
  min-height: 100vh;
  background: #f9fafb;
  padding: 2rem 0;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
}

.header {
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
}

.header h1 {
  font-size: 2.5rem;
  color: #111827;
  margin-bottom: 0.5rem;
}

.last-updated {
  color: #6b7280;
  font-size: 0.9rem;
}

.content {
  background: #ffffff;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

section {
  margin-bottom: 2.5rem;
}

section:last-child {
  margin-bottom: 0;
}

h2 {
  font-size: 1.5rem;
  color: #111827;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

h3 {
  font-size: 1.2rem;
  color: #111827;
  margin: 1.5rem 0 0.75rem 0;
}

p {
  color: #374151;
  line-height: 1.8;
  margin-bottom: 1rem;
}

ul {
  color: #374151;
  line-height: 1.8;
  margin: 1rem 0 1rem 2rem;
}

li {
  margin-bottom: 0.5rem;
}

li strong {
  color: #111827;
}

.cookie-category {
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
}

.cookie-category.essential {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
}

.cookie-category.functional {
  background: #d1ecf1;
  border-left: 4px solid #17a2b8;
}

.cookie-category.analytics {
  background: #d4edda;
  border-left: 4px solid #28a745;
}

.cookie-category.marketing {
  background: #f8d7da;
  border-left: 4px solid #dc3545;
}

.tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.85rem;
  margin-right: 0.5rem;
  background: rgba(0, 0, 0, 0.1);
}

.cookie-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.9rem;
}

.cookie-table thead {
  background: #f3f4f6;
}

.cookie-table th {
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #111827;
  border-bottom: 2px solid #e5e7eb;
}

.cookie-table td {
  padding: 0.75rem;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.cookie-table tbody tr:hover {
  background: #f3f4f6;
}

.preference-actions {
  margin: 1.5rem 0;
  text-align: center;
}

.preference-actions .btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.icon {
  font-size: 1.2rem;
}

.contact-info {
  background: #f3f4f6;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.contact-info p {
  margin-bottom: 0.5rem;
}

a {
  color: #2563eb;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.footer-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #e5e7eb;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
  border: none;
  cursor: pointer;
  font-size: 1rem;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
  text-decoration: none;
  transform: translateY(-2px);
}

.btn-secondary {
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f3f4f6;
  text-decoration: none;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .header h1 {
    font-size: 2rem;
  }

  .content {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.3rem;
  }

  .cookie-table {
    font-size: 0.8rem;
  }

  .cookie-table th,
  .cookie-table td {
    padding: 0.5rem;
  }

  .footer-actions {
    flex-direction: column;
  }
}
</style>
